import React, { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { isNativePlatform } from '../native/platform';

// Parse the QR payload produced by EventTicket.js
const parseTicket = (raw) => {
  try {
    const data = JSON.parse(raw);
    if (data?.registrationId || data?.ticketCode) return data;
  } catch (_) {
    // Fall back to treating the raw value as a bare ticket code
    if (raw && typeof raw === 'string') return { ticketCode: raw.trim() };
  }
  return null;
};

const CheckInScanner = () => {
  const { user } = useAuth();
  const [result, setResult] = useState(null); // { type, message, attendee }
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');

  // Look up the registration and mark it attended.
  const checkIn = useCallback(async (ticket) => {
    if (!ticket) {
      setResult({ type: 'error', message: 'Unrecognized ticket code.' });
      return;
    }

    setResult({ type: 'loading', message: 'Verifying ticket...' });

    try {
      let query = supabase.from('event_registrations').select('*');
      if (ticket.registrationId) {
        query = query.eq('id', ticket.registrationId);
      } else if (ticket.ticketCode) {
        query = query.eq('ticket_code', ticket.ticketCode);
      }

      const { data: rows, error } = await query.limit(1);
      if (error) throw error;

      const reg = rows?.[0];
      if (!reg) {
        setResult({ type: 'error', message: 'Ticket not found.' });
        return;
      }

      if (reg.status === 'cancelled') {
        setResult({ type: 'error', message: 'This registration was cancelled.' });
        return;
      }

      if (reg.checked_in_at) {
        setResult({
          type: 'warning',
          message: `Already checked in at ${new Date(reg.checked_in_at).toLocaleTimeString()}.`,
          attendee: reg.attendee_name || ticket.attendeeName,
        });
        return;
      }

      const { error: updateError } = await supabase
        .from('event_registrations')
        .update({
          status: 'attended',
          checked_in_at: new Date().toISOString(),
          checked_in_by: user?.id || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', reg.id);

      if (updateError) throw updateError;

      setResult({
        type: 'success',
        message: 'Checked in successfully.',
        attendee: reg.attendee_name || ticket.attendeeName || 'Attendee',
      });
    } catch (err) {
      console.error('Check-in failed:', err);
      setResult({ type: 'error', message: 'Check-in failed. Please try again.' });
    }
  }, [user?.id]);

  const startScan = useCallback(async () => {
    if (!isNativePlatform()) {
      setResult({
        type: 'error',
        message: 'Camera scanning is only available in the mobile app. Use manual entry below.',
      });
      return;
    }

    let BarcodeScanner;
    try {
      ({ BarcodeScanner } = await import('@capacitor-mlkit/barcode-scanning'));
    } catch (err) {
      setResult({ type: 'error', message: 'Scanner plugin unavailable.' });
      return;
    }

    try {
      const { camera } = await BarcodeScanner.requestPermissions();
      if (camera !== 'granted' && camera !== 'limited') {
        setResult({ type: 'error', message: 'Camera permission denied.' });
        return;
      }

      setScanning(true);
      const { barcodes } = await BarcodeScanner.scan();
      setScanning(false);

      const raw = barcodes?.[0]?.rawValue;
      if (!raw) {
        setResult({ type: 'error', message: 'No QR code detected.' });
        return;
      }
      await checkIn(parseTicket(raw));
    } catch (err) {
      setScanning(false);
      console.error('Scan error:', err);
      setResult({ type: 'error', message: 'Scan cancelled or failed.' });
    }
  }, [checkIn]);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    checkIn({ ticketCode: manualCode.trim() });
    setManualCode('');
  };

  const resultStyles = {
    success: 'bg-green-50 text-green-800 border-green-200',
    warning: 'bg-amber-50 text-amber-800 border-amber-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    loading: 'bg-gray-50 text-gray-700 border-gray-200',
  };

  return (
    <div className="max-w-md mx-auto p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Event Check-In</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Scan an attendee's ticket QR code to check them in.
        </p>

        <button
          onClick={startScan}
          disabled={scanning}
          className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-medium rounded-xl transition-colors flex items-center justify-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h4M3 16v4a1 1 0 001 1h4m8-18h4a1 1 0 011 1v4m0 8v4a1 1 0 01-1 1h-4M4 12h16" />
          </svg>
          {scanning ? 'Scanning...' : 'Scan Ticket QR'}
        </button>

        <form onSubmit={handleManualSubmit} className="mt-4">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
            Or enter ticket code
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="e.g. A1B2C3D4"
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none font-mono uppercase"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-lg"
            >
              Check
            </button>
          </div>
        </form>

        {result && (
          <div className={`mt-6 p-4 rounded-xl border ${resultStyles[result.type]}`}>
            <p className="font-medium">{result.message}</p>
            {result.attendee && (
              <p className="text-sm mt-1">{result.attendee}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckInScanner;
